import { DEFAULT_VISUAL_STYLE, VISUAL_STYLES } from "../visual/styleProfiles";
import * as THREE from "three";
import { VolumetricAtmosphere, VOLUME_COMPOSITE_GLSL, type AtmosphereFrame } from "./volumetricAtmosphere";
import { perf } from "../engine/perf";

/**
 * Painterly post — the screen-space half of the painted look.
 *
 * The reference this is modelled on (a Ghibli-valley pen) gets its look from two
 * places, and it is worth being precise about which is which, because only one of
 * them belongs in a post pass:
 *
 *   1. A painted LIGHT MODEL in every material — banded terminators with a
 *      hand-drawn wobble, shadows that shift hue instead of going black, ambient
 *      that tints instead of washing. That lives per-material; see
 *      world/stylizedShading.ts.
 *   2. A painted PRINT over the finished frame — distance dissolving into
 *      wet-in-wet softness, chroma running past its edges, a filmic roll-off,
 *      shadows pushed violet and highlights pushed cream, paper tooth, a warm
 *      vignette. That is this file.
 *
 * (2) alone already reads as "a painting of the game" rather than "the game";
 * (1) is what makes it read as a painting rather than a filter. They are
 * deliberately separable so each can be judged — and costed — on its own.
 *
 * COST. One multisampled scene target, one eighth-res downsample, two eighth-res
 * blurs, one full-screen composite. The blur chain runs at 1/64 the pixels so it
 * is nearly free; the composite is the real spend, and it is a single pass with
 * a handful of fetches. Everything scales with `paint`, and at `paint = 0` the
 * composite is a bit-exact passthrough (tonemap included), so A/B is honest.
 *
 * Three details that matter when wrapping an existing renderer:
 *   - MSAA. Rendering into an ordinary target throws away the antialiasing the
 *     canvas was created with, which is glaring on low-poly silhouettes, so the
 *     scene target is multisampled rather than resolved with an FXAA pass.
 *   - Colour space, and this one bites. Three forces LinearSRGB output for every
 *     non-XR render target, so simply rendering offscreen moves the whole game's
 *     ALPHA AND ADDITIVE BLENDING from sRGB-encoded values (what the default
 *     framebuffer does, and what every effect in this client was authored
 *     against) into linear — snow, bolts, jets and glows all jump. That is a
 *     separate argument from the painted look, so `blendSpace` keeps it
 *     separate: "srgb" reproduces the old compositing exactly and "linear" is
 *     the physically-correct destination, once the FX have been retuned for it.
 *     See BLEND_SPACE_NOTE below for how "srgb" is obtained.
 *   - Depth. Distance is read from a depth texture and folded through the SAME
 *     exponential-squared curve the scene's fog uses, so the softening tracks
 *     the atmosphere for free: full strength in thick valley air, and exactly
 *     zero in space, where the fog density is ~1e-9. No extra tuning knob, and
 *     no chance of the two disagreeing.
 */

/** Live tuning, seeded from the approved natural preset. The original painted
 * treatment remains in VISUAL_STYLES.current for A/B. Every term scales by paint. */
export const PAINTERLY = {
  /** Master 0..1. At 0 the composite is a passthrough — the honest A/B. */
  paint: 1,
  /** Pre-tonemap exposure. The filmic curve below is built around 1.0. */
  exposure: 1.0,
  /** Paper tooth strength (multiplier on the baked grain). */
  grain: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.grain,
  /** Warm-dark corner falloff. */
  vignette: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.vignette,
  /** Chromatic aberration at the rim. Small — this is a lens hint, not a glitch. */
  ca: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.ca,
  /** How far the wet-in-wet softening is allowed to go at full fog. */
  soften: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.soften,
  /**
   * Which space the SCENE composites in — see BLEND_SPACE_NOTE.
   *   "srgb"   — what the default framebuffer does, and what every additive
   *              effect in this client was authored against. The default,
   *              because it keeps the painted look the only thing that changed.
   *   "linear" — physically correct, and where we want to end up; expect glows,
   *              bolts, jets and precipitation to read considerably hotter until
   *              they are retuned.
   * Changing it rebuilds the chain, so it is fine to flip live from the console.
   */
  blendSpace: "srgb" as "srgb" | "linear",
  /** Midtone saturation of the grade. 1 is the original tuning; the default
   *  sits above it because the grade's haze + chroma bleed read a touch grey. */
  sat: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.sat,
  /** Warm limestone highlights, cool blue shade; gated by scene illumination. */
  heritage: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.heritage,
  /** Restrained bright-light spill using the existing blur, with no extra pass. */
  bloom: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.bloom,
  /**
   * INK — sketched line work over depth edges (see the ink block in the
   * composite). 0 disables it; like every other term it is also scaled by
   * `paint`, so the paint = 0 passthrough stays bit-exact. Off by default:
   * world shapes read through lighting and color rather than black contours.
   */
  ink: VISUAL_STYLES[DEFAULT_VISUAL_STYLE].post.ink,
  /** Line thickness, in texels of the half-res mask (so 1 ≈ two frame pixels,
   *  resolution-compensated). */
  inkWidth: 1,
  /** 0 = continuous contour lines (the default), 1 = fully noise-broken pen
   *  strokes. */
  inkBreak: 0,
};

/**
 * BLEND_SPACE_NOTE — how "srgb" is obtained.
 *
 * Three decides a render pass's output colour space with, effectively:
 *
 *   target === null ? renderer.outputColorSpace
 *                   : target.isXRRenderTarget ? target.texture.colorSpace
 *                                             : LinearSRGBColorSpace
 *
 * so an ordinary target always gets linear, and there is no supported knob that
 * says otherwise. Marking the target `isXRRenderTarget` takes the middle branch,
 * which means (a) the materials emit their sRGB encode exactly as they do when
 * drawing to the canvas, and (b) `getInternalFormat` is passed
 * forceLinearTransfer = true, so the storage stays RGBA8 and the GPU does NOT
 * re-encode on write. Encoded values in a linear buffer: blending matches the
 * default framebuffer bit for bit, and the composite decodes on the way in.
 *
 * It is a flag three carries for its own XR targets rather than a public API,
 * and every other place it is read (tone mapping, internal format) wants the
 * same answer we do. If a three upgrade ever changes that, `_painterly.mjs`
 * catches it: the paint = 0 passthrough check fails the moment blending moves.
 */

/** Below this the speed lens is skipped entirely. */
const SPEED_THRESHOLD = 0.004;
const SAMPLES = 4;
/** Soft ("wet-in-wet") chain resolution, as a right-shift of the frame size. */
const SOFT_SHIFT = 3;

const QUAD_VS = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

/** Full-res → 1/8 in one blit, four bilinear taps in a rotated box. The result is
 *  gaussian-blurred immediately afterwards and only ever mixed into distance, so
 *  the aliasing this leaves never survives to the screen. */
const DOWN_FS = /* glsl */ `
  uniform sampler2D uSrc;
  uniform vec2 uTexel;   // 1 / SOURCE size
  varying vec2 vUv;
  void main() {
    vec2 o = uTexel * 2.0;
    vec3 c = texture2D(uSrc, vUv + vec2( o.x,  o.y)).rgb
           + texture2D(uSrc, vUv + vec2(-o.x,  o.y)).rgb
           + texture2D(uSrc, vUv + vec2( o.x, -o.y)).rgb
           + texture2D(uSrc, vUv + vec2(-o.x, -o.y)).rgb;
    gl_FragColor = vec4(c * 0.25, 1.0);
  }
`;

/** Separable gaussian, five taps riding the bilinear unit so it covers nine. */
const BLUR_FS = /* glsl */ `
  uniform sampler2D uSrc;
  uniform vec2 uDir;     // (1/w, 0) or (0, 1/h)
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uSrc, vUv).rgb * 0.2270270270;
    vec2 o1 = uDir * 1.3846153846;
    vec2 o2 = uDir * 3.2307692308;
    c += (texture2D(uSrc, vUv + o1).rgb + texture2D(uSrc, vUv - o1).rgb) * 0.3162162162;
    c += (texture2D(uSrc, vUv + o2).rgb + texture2D(uSrc, vUv - o2).rgb) * 0.0702702703;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// Window-space depth → view depth (positive, metres). The game runs a plain
// depth buffer per partition slice (see world/depthPartition.ts); the harness
// path may run logarithmic. Both are exact, and which is compiled in is
// decided from the renderer's own capabilities rather than guessed. Shared by
// the ink mask and the composite — they must decode identically.
const DEPTH_DECODE_GLSL = /* glsl */ `
  float linearDepth(float dep) {
  #ifdef LOG_DEPTH
    return exp2(dep * uDepthK.x) - 1.0;   // uDepthK.x = log2(far + 1)
  #else
    // uDepthK = (near, far, far - near)
    float ndc = dep * 2.0 - 1.0;
    return 2.0 * uDepthK.x * uDepthK.y / (uDepthK.y + uDepthK.x - ndc * uDepthK.z);
  #endif
  }
`;

/**
 * Ink pre-pass: the full-res depth + consent reduced to a half-res field the
 * mask pass can sample EXACTLY. This pass exists because of two bugs that both
 * came from skipping it:
 *
 *  - A half-res pass sampling the FULL-res depth texture puts every sample on
 *    the corner BETWEEN four texels; NEAREST resolves the tie by rounding, and
 *    sub-pixel motion re-rounds it differently every frame. That was the
 *    "gritty, aliased, jittering" ink: the detector wasn't seeing the scene,
 *    it was seeing a coin flip per texel.
 *  - Sampling the consent alpha the same way (or bilinearly) DILUTES it: a
 *    meadow reads ~0.5 instead of 0, and the grass the per-material weight had
 *    opted out came back at half strength.
 *
 * The reduction is MIN on both channels, and min is load-bearing twice over.
 * Min depth means "the nearest surface anywhere under this texel", so a thin
 * feature can never fall between subsample points and pop; the mask's grid is
 * then this field's own grid, every tap lands on a texel centre, and nothing
 * is left for the hardware to round. Min consent means one grass pixel under
 * the texel is enough to lift the pen — conservative in exactly the direction
 * the art wants. Linear depth (metres) in half-float: 10 mantissa bits ≈ 0.05%
 * relative error, ~60x under the edge threshold.
 */
const INK_PRE_FS = /* glsl */ `
  uniform sampler2D uDepth;
  uniform sampler2D uScene;
  uniform vec2 uRes;       // FULL-res frame size
  uniform vec2 uMaskRes;   // this target's size
  uniform vec3 uDepthK;
  varying vec2 vUv;

  ${DEPTH_DECODE_GLSL}

  void main() {
    // Top-left full-res texel of this half-res texel's 2x2 block, then the
    // four texel CENTRES — never a boundary. The consent that survives is the
    // NEAREST sample's, not the block minimum: a line visually belongs to the
    // nearest surface at an edge, so it is that surface's consent that should
    // decide. (Min consent lifted the pen whenever grass was merely BEHIND
    // something — the player's own outline vanished against a meadow.)
    vec2 t = floor(vUv * uMaskRes) * 2.0;
    vec2 b = (t + 0.5) / uRes;
    vec2 o = 1.0 / uRes;
    float z = linearDepth(texture2D(uDepth, b).x);
    float a = texture2D(uScene, b).a;
    float z2;
    z2 = linearDepth(texture2D(uDepth, b + vec2(o.x, 0.0)).x);
    if (z2 < z) { z = z2; a = texture2D(uScene, b + vec2(o.x, 0.0)).a; }
    z2 = linearDepth(texture2D(uDepth, b + vec2(0.0, o.y)).x);
    if (z2 < z) { z = z2; a = texture2D(uScene, b + vec2(0.0, o.y)).a; }
    z2 = linearDepth(texture2D(uDepth, b + o).x);
    if (z2 < z) { z = z2; a = texture2D(uScene, b + o).a; }
    gl_FragColor = vec4(min(z, 1000.0), a, 0.0, 1.0);
  }
`;

/**
 * The ink mask, computed at HALF resolution over the pre-pass field — and half
 * resolution is the point, not a cost cut. A full-res line derived from
 * single-sample depth is one pixel wide and binary: as wind or planet motion
 * sweeps an edge sub-pixel, the line POPS from one pixel column to the next.
 * Down here each texel aggregates a 2x2 neighbourhood (see INK_PRE_FS) and the
 * composite samples the result BILINEARLY, so the same sweep plays out as a
 * partial value sliding across texels — the line is thick (which the look
 * wants anyway), soft-edged, and holds still.
 *
 * The edge measure is the second difference of RECIPROCAL depth: 1/z is linear
 * in screen space on any plane, so the measure is exactly zero across flat
 * ground and walls at every viewing angle, and spikes only where the surface
 * actually changes — silhouettes (huge) and creases like roof lines (small but
 * clean). Two rings must agree (1 and 3 texels): a structure thinner than the
 * wide ring — a grass blade, a wire — vanishes between its taps, and a real
 * form does not.
 */
const INK_FS = /* glsl */ `
  uniform sampler2D uPre;  // r = min linear depth (m), g = min ink consent
  uniform vec2 uMaskRes;   // THIS target's size (half the frame)
  uniform float uInkWidth;
  varying vec2 vUv;

  // Consent rides ALONG WITH the depth taps — gating only at the line's own
  // texel let taps read a grass clump's chaotic depth from three texels away
  // and draw a halo around it. But it is the NEAREST tap's consent that
  // decides, not the minimum over all taps: a line belongs to the nearest
  // surface at the edge, so grass IN FRONT lifts the pen (its silhouette is
  // chaos) while grass merely BEHIND does not (the player's outline against a
  // meadow is the player's line, and it stays).
  float inkEdge(vec2 uv, vec2 tx, float zC, float inkT, inout float zMin, inout float aNear) {
    vec2 L = texture2D(uPre, uv - vec2(tx.x, 0.0)).rg;
    vec2 R = texture2D(uPre, uv + vec2(tx.x, 0.0)).rg;
    vec2 D = texture2D(uPre, uv - vec2(0.0, tx.y)).rg;
    vec2 U = texture2D(uPre, uv + vec2(0.0, tx.y)).rg;
    if (L.r < zMin) { zMin = L.r; aNear = L.g; }
    if (R.r < zMin) { zMin = R.r; aNear = R.g; }
    if (D.r < zMin) { zMin = D.r; aNear = D.g; }
    if (U.r < zMin) { zMin = U.r; aNear = U.g; }
    float wC = 1.0 / zC;
    float ex = abs(1.0 / L.r + 1.0 / R.r - 2.0 * wC) * zC;
    float ey = abs(1.0 / D.r + 1.0 / U.r - 2.0 * wC) * zC;
    // GRAZING COMPENSATION, per axis — the depth-only stand-in for the
    // reference's normal-based threshold (they raise it by 1 - normal.z).
    // Ground seen at a shallow angle has an enormous depth slope per texel,
    // so gentle terrain facets at range measured as creases and striped the
    // distance with contour lines. The ONE-SIDED MINIMUM gradient is the
    // discriminator: on smooth grazing ground both sides slope hard (raise
    // the threshold), while at a true silhouette the same-surface side is
    // flat (threshold unchanged, the line survives).
    float gx = min(abs(L.r - zC), abs(R.r - zC)) / zC;
    float gy = min(abs(D.r - zC), abs(U.r - zC)) / zC;
    float tX = inkT * (1.0 + 60.0 * gx);
    float tY = inkT * (1.0 + 60.0 * gy);
    // No "both axes fired" suppression here, and its absence is deliberate: a
    // slightly angled straight edge is a STAIRCASE on this grid, every stair
    // corner fires both axes, and suppressing those pixels turned every long
    // clean line into evenly spaced dashes.
    return max(smoothstep(tX, tX * 3.0, ex), smoothstep(tY, tY * 3.0, ey));
  }

  void main() {
    vec2 pre = texture2D(uPre, vUv).rg;
    float zC = pre.r;
    float line = 0.0;
    if (zC < 340.0) {
      // Integer texel steps, so every ring tap lands on a texel centre of the
      // pre-field — the whole point of having the pre-pass.
      float wPx = max(floor(uInkWidth * min(1.0, uMaskRes.y / 650.0) + 0.5), 1.0);
      vec2 tx = wPx / uMaskRes;
      // The threshold RISES with distance: a texel spans more world out there,
      // so the same gentle facet-to-facet crease measures larger — left flat,
      // every low-poly terrain facet at range stipples into pepper.
      float inkT = 0.03 * (1.0 + zC * 0.012);
      float zMin = zC;
      float aNear = pre.g;
      float edge = inkEdge(vUv, tx, zC, inkT, zMin, aNear);
      // The wide ring is a GATE, not a modulator: min() of the two rings let
      // the wide ring's phase against an angled edge amplitude-modulate the
      // line into dashes. As a gate it still rejects thin structure (a blade
      // or a wire measures ~0 at 3 texels), but any solid form passes at full
      // strength and the line stays continuous.
      if (edge > 0.01) {
        float wide = inkEdge(vUv, tx * 3.0, zC, inkT * 2.5, zMin, aNear);
        edge *= smoothstep(0.06, 0.30, wide);
      }
      // Fade on the nearest tap — a near silhouette against far sky keeps its
      // edge, drawn on the near side — and long before the 600 m slice bound.
      float fadeD = 1.0 - smoothstep(100.0, 320.0, zMin);
      // Consent of the NEAREST surface this line's measure touched, with a
      // hard shoulder: an edge whose near side is even half grass gets no
      // half-line, it gets none.
      float inkW = smoothstep(0.55, 0.9, aNear);
      line = edge * inkW * fadeD;
    }
    gl_FragColor = vec4(line, 0.0, 0.0, 1.0);
  }
`;

const COMPOSITE_FS = /* glsl */ `
  uniform sampler2D uScene;
  uniform sampler2D uSoft;
  uniform sampler2D uDepth;
  uniform sampler2D uGrainTex;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform vec3 uDepthK;       // depth → view-Z constants; see linearDepth()
  uniform float uFogDensity;  // scene.fog.density; 0 disables the distance softening
  uniform float uPaint;
  uniform float uExposure;
  uniform float uGrain;
  uniform float uVignette;
  uniform float uCA;
  uniform float uSoften;
  uniform float uSpeed;
  uniform float uSat;
  uniform float uHeritage;
  uniform float uBloom;
  uniform float uInk;
  uniform float uInkBreak;
  uniform float uInkDay;   // 0 night → 1 day; blends the shadow gate in
  uniform sampler2D uInkTex;
  uniform vec2 uCamTan;    // tan(fov/2) * aspect, tan(fov/2)
  uniform mat3 uCamBasis;  // camera world basis: right, up, +Z (backward)
  uniform vec3 uInkSnap;   // camera world pos modulo the ink noise tile
  varying vec2 vUv;

  float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

  vec3 toSRGB(vec3 c) {
    return mix(c * 12.92,
               1.055 * pow(max(c, vec3(1e-5)), vec3(1.0 / 2.4)) - 0.055,
               step(vec3(0.0031308), c));
  }

  // Scene → working space, and back out to the screen.
  //
  // In SRGB_SCENE the buffer already holds encoded values (see
  // BLEND_SPACE_NOTE), so the composite has to decode before it can grade. It
  // uses gamma 2 rather than the true 2.4 curve, for two reasons: it is a
  // multiply against a pow, and — the point — sqrt(c*c) is c, so the round trip
  // is EXACT and paint = 0 stays the honest passthrough it claims to be. The
  // half-stop of curve error against true sRGB lands entirely inside a
  // deliberately stylised grade.
  #ifdef SRGB_SCENE
    vec3 toWork(vec3 c) { return c * c; }
    vec3 toScreen(vec3 c) { return sqrt(max(c, vec3(0.0))); }
  #else
    vec3 toWork(vec3 c) { return c; }
    vec3 toScreen(vec3 c) { return toSRGB(c); }
  #endif

  ${DEPTH_DECODE_GLSL}
  ${VOLUME_COMPOSITE_GLSL}
  vec3 fetchScene(vec2 uv) {
    return applyAtmosphere(toWork(texture2D(uScene, uv).rgb), uv, linearDepth(texture2D(uDepth, uv).r));
  }

  // A filmic roll-off with a long shoulder — highlights bend rather than clip,
  // which is what keeps a bright sky reading as paint instead of as paper.
  vec3 tonemap(vec3 x) {
    x = max(x, vec3(0.0));
    vec3 a = x * (x * 0.36 + 0.42);
    vec3 b = x * (x * 0.34 + 0.66) + 0.11;
    return clamp(a / b, 0.0, 1.0);
  }

  // Speed lens: chromatic fringing along the radius. There is deliberately NO
  // radial smear — it was tried, and at any strength where it registered it also
  // destroyed the readability of the terrain being steered into, which matters
  // more on a vehicle than the effect does. Dropping it also took the lens from
  // eighteen fetches to three.
  vec3 speedLens(vec2 uv, float k) {
    vec2 fringe = (uv - 0.5) * (0.007 * k);
    return vec3(
      fetchScene(uv + fringe).r,
      fetchScene(uv).g,
      fetchScene(uv - fringe).b
    );
  }

  void main() {
    vec2 uv = vUv;
    vec2 d = uv - 0.5;
    d.x *= uAspect;
    float r2 = dot(d, d);

    // ── the speed lens, folded in rather than given a pass of its own ──────
    float lensK = 0.0;
    if (uSpeed > ${SPEED_THRESHOLD.toFixed(4)}) {
      // PERIPHERY ONLY. The old band reached full strength at r2 = 0.29 — barely
      // half way out — so the lens covered most of the screen and read as a
      // global filter. It now stays at exactly zero across the middle of the
      // frame, where the player is looking and steering, and only arrives in the
      // outer corners.
      lensK = uSpeed * smoothstep(0.35, 1.0, r2);
      // Barrel bow: magnify the centre slightly, which bends straight edges the
      // way a wide lens does.
      uv = vUv - (vUv - 0.5) * (0.09 * lensK);
    }
    vec3 c = lensK > 0.001 ? speedLens(uv, lensK) : fetchScene(uv);

    // ── how far away is this pixel, in the fog's own terms ─────────────────
    // The log depth buffer stores log2(1 + w) normalised by log2(far + 1); undo
    // that to recover view depth, then run it through three's own FogExp2 curve
    // so "wet" means exactly "as hazed as the material already drew it".
    float dep = texture2D(uDepth, uv).x;
    float zC = linearDepth(dep);
    float fd = uFogDensity * zC;
    float wet = 1.0 - exp(-fd * fd);
    wet = clamp(wet, 0.0, 1.0);

    vec3 soft = toWork(texture2D(uSoft, uv).rgb);

    // ── how much light is actually HERE ────────────────────────────────────
    // The whole grade below — the violet in the shadows, the cream in the
    // highlights, the lifted black — describes what happens to PAINT under
    // light. Applied unconditionally it turns a night sky into milky mauve and
    // a starfield into confetti, because it is asserting bounce light that the
    // scene does not have. The blurred frame is already in hand and is exactly
    // the right question to ask: how bright is the neighbourhood? Lit ground
    // gets the full treatment; a dark sky, a cave, deep space get almost none,
    // and the transition costs a smoothstep.
    float lightHere = smoothstep(0.02, 0.30, luma(soft));
    float grade = uPaint * lightHere;

    // ── a whisper of chromatic aberration at the rim ───────────────────────
    // Kept small, and kept out of the dark: a two-pixel split is invisible on
    // a hillside and reads as rainbow confetti on a field of one-pixel stars.
    {
      float ca = uCA * grade * (0.0003 + r2 * 0.0016);
      vec3 src = fetchScene(uv);
      c.r += fetchScene(uv + d * ca).r - src.r;
      c.b += fetchScene(uv - d * ca).b - src.b;
    }

    // ── watercolour softening with distance (wet-in-wet, not bokeh) ────────
    // Held back in the dark for a different reason than the grade above: at full
    // strength it mixes 42% of a BLURRED starfield over the sharp one, and a
    // smeared star is just grey. A floor rather than a gate — a night should
    // still be soft, it just should not be washed.
    c = mix(c, soft, wet * uSoften * uPaint * mix(0.3, 1.0, lightHere));
    // Warm atmospheric highlight spill, gated out of night and dark interiors.
    c += max(soft - vec3(0.52), vec3(0.0)) * uBloom * uPaint * grade * uInkDay;

    // ── chroma bleed: paint runs, pixels do not ────────────────────────────
    // Belongs to distance, like the softening it accompanies — a flat amount
    // everywhere quietly smears the colour of near detail too.
    {
      float lc = luma(c);
      vec3 chroma = soft - vec3(luma(soft));
      c = mix(c, vec3(lc) + chroma, (0.025 + 0.075 * wet) * uPaint);
    }

    // ── ink: line work over depth edges ────────────────────────────────────
    // Modelled on messenger.abeto.co, which draws its comic outlines from a
    // surface-ID buffer every material writes. This client's version needs no
    // MRT: the lines are detected in a HALF-RES mask pass (see INK_FS for the
    // measure and for why half res is what keeps them still), sampled here
    // bilinearly, so they arrive thick and soft-edged.
    //
    // Art direction (user-set): the ink is BLACK, the lines are CONTINUOUS by
    // default (uInkBreak opts back into noise-broken strokes), and SHADOW GETS
    // NO LINES — inkLit below scales the pen with the local light. The gate's
    // 0.75 / 0.5 height coefficients are not arbitrary: the snap tile (64 m)
    // times each must land on a whole number of grain repeats (16 m), or the
    // pattern re-rolls when the snap steps.
    //
    // THE ANCHOR IS THE FOOT PLANET'S LOCAL FRAME, not render space, and that
    // distinction was learned by watching every line in a town strobe: planets
    // SPIN, so a pattern fixed in render space sweeps across the geometry at
    // surface speed and the gaps race visibly along each line. uCamBasis and
    // uInkSnap are pre-multiplied by the planet's inverse rotation on the CPU,
    // so the position reconstructed here lands in planet-local metres and the
    // gaps stay put on the wall they were drawn on. Off-planet the frame falls
    // back to plain render space — right for a ship interior, where it is the
    // ship that moves and nothing world-anchored would stick to it anyway.
    float inkK = uInk * uPaint;
    if (inkK > 0.001) {
      // The 1.35 gain buys back the strength the bilinear upsample spreads
      // into the line's soft shoulders; the min keeps the core at exactly 1.
      float line = min(texture2D(uInkTex, uv).r * 1.35, 1.0);
      if (line > 0.01) {
        float gate = 1.0;
        if (uInkBreak > 0.001) {
          vec2 nd = uv * 2.0 - 1.0;
          vec3 wp = uInkSnap + (uCamBasis[0] * (nd.x * uCamTan.x)
                              + uCamBasis[1] * (nd.y * uCamTan.y)
                              - uCamBasis[2]) * zC;
          vec2 nuv = vec2(wp.x + wp.y * 0.75, wp.z - wp.y * 0.5) * (1.0 / 16.0);
          gate = mix(1.0, smoothstep(0.38, 0.62, texture2D(uGrainTex, nuv).g), uInkBreak);
        }
        // NO INK IN SHADOW — BUT NIGHT IS NOT SHADOW. The blurred frame
        // answers "is there light here", and BY DAY the pen scales with the
        // answer: a lit wall keeps its full line, a wall inside a cast shadow
        // keeps none. Luminance alone cannot tell a shadowed noon from a clear
        // midnight, so the DAY FACTOR (from the same lighting state that runs
        // the sky) blends the gate out as the sun sets: at night every border
        // is drawn, full black — which stays correct-looking because the ink
        // IS black. Mixing toward zero can only darken, so no clamp is needed,
        // and the grade's gated lift keeps a lit line from reading as a void.
        float inkLit = mix(1.0, smoothstep(0.05, 0.30, luma(soft)), uInkDay);
        c = mix(c, vec3(0.0), line * gate * inkLit * inkK);
      }
    }

    // ── the print ──────────────────────────────────────────────────────────
    c = mix(c, tonemap(c * uExposure), uPaint);

    // Subtle cool shade / warm light, preserving the material's own pigment.
    float l = luma(c);
    vec3 shadowPush = mix(vec3(0.97, 0.99, 1.04), vec3(1.0), smoothstep(0.0, 0.34, l));
    vec3 highPush   = mix(vec3(1.0), vec3(1.018, 1.005, 0.98), smoothstep(0.44, 0.98, l));
    c *= mix(vec3(1.0), shadowPush, 0.85 * grade) * mix(vec3(1.0), highPush, 0.9 * uPaint);

    // Reference palette: warm sunlight and blue shade, with deeper value separation.
    // Pixel-light gating keeps night/cave exposure and emissive colors legible.
    vec3 referenceShade=mix(vec3(0.91,0.97,1.10),vec3(1.0),smoothstep(0.05,0.48,l));
    vec3 referenceSun=mix(vec3(1.0),vec3(1.055,1.012,0.92),smoothstep(0.35,0.95,l));
    c *= mix(vec3(1.0),referenceShade*referenceSun,uHeritage*grade);

    // A small daylight bounce floor keeps shaded pigment readable without
    // turning dark greens, bark and stone into pale lavender.
    vec3 lift = vec3(0.002, 0.003, 0.004) * grade;
    c = c * (1.0 - lift) + lift;

    // Stronger daylight value separation; retain night/cave shadow detail.
    c = mix(c, c * c * (3.0 - 2.0 * c), mix(0.32,0.45,uHeritage) * grade);
    l = luma(c);
    float satBoost = 1.0 + 0.16 * uSat * grade * smoothstep(0.10, 0.42, l) * (1.0 - smoothstep(0.62, 0.96, l));
    c = mix(vec3(l), c, satBoost);

    // ── paper tooth ────────────────────────────────────────────────────────
    // Baked once into a tiling texture rather than evaluated as noise: this is a
    // ±3% multiplier that runs on every pixel of the frame, and one fetch beats
    // two gradient-noise evaluations by an order of magnitude.
    {
      vec3 g = texture2D(uGrainTex, uv * uRes / 256.0 * 0.42).rgb;
      float amt = uGrain * uPaint;
      c *= 1.0 + (g.r - 0.5) * 0.060 * amt + (g.g - 0.5) * 0.030 * amt + (g.b - 0.5) * 0.020 * amt;
    }

    // ── vignette, warm-dark ────────────────────────────────────────────────
    float vig = pow(clamp(1.0 - r2 * 1.15, 0.0, 1.0), 1.55);
    float vigAmt = uVignette * uPaint + lensK * 0.34;
    c *= mix(vec3(1.0), mix(vec3(0.62, 0.60, 0.66), vec3(1.0), vig), clamp(vigAmt, 0.0, 1.0));

    c = toScreen(clamp(c, 0.0, 1.0));

    // ── ordered dither: the sky must never band ────────────────────────────
    float dth = fract(dot(gl_FragCoord.xy, vec2(0.7548776662, 0.5698402909)));
    c += (dth - 0.5) / 255.0;
    gl_FragColor = vec4(c, 1.0);
  }
`;

/** Paper tooth, baked once: r = fine grain, g = coarse blotch, b = horizontal fibre. */
function makeGrainTexture(): THREE.DataTexture {
  const N = 256;
  const data = new Uint8Array(N * N * 4);
  // A tiny deterministic hash so the tooth is the same on every machine and in
  // every screenshot — grain that changes between runs makes visual diffs useless.
  const hash = (x: number, y: number, s: number): number => {
    let h = (x * 374761393 + y * 668265263 + s * 1274126177) | 0;
    h = (h ^ (h >>> 13)) * 1274126177;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
  };
  const smooth = (x: number, y: number, cell: number, s: number): number => {
    const fx = x / cell, fy = y / cell;
    const ix = Math.floor(fx), iy = Math.floor(fy);
    const tx = fx - ix, ty = fy - iy;
    const ux = tx * tx * (3 - 2 * tx), uy = ty * ty * (3 - 2 * ty);
    const w = N / cell;
    const a = hash(ix % w, iy % w, s), b = hash((ix + 1) % w, iy % w, s);
    const c = hash(ix % w, (iy + 1) % w, s), e = hash((ix + 1) % w, (iy + 1) % w, s);
    return (a + (b - a) * ux) + ((c + (e - c) * ux) - (a + (b - a) * ux)) * uy;
  };
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const i = (y * N + x) * 4;
      data[i] = Math.round(hash(x, y, 1) * 255); // fine tooth
      data[i + 1] = Math.round(smooth(x, y, 8, 2) * 255); // coarse blotch
      // Fibre runs across the sheet: long in x, tight in y.
      data[i + 2] = Math.round(smooth(x, y, 32, 3) * 0.5 * 255 + smooth(x, y * 8, 4, 4) * 0.5 * 255);
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export class PainterlyPost {
  private sceneRT: THREE.WebGLRenderTarget | null = null;
  private readonly atmosphere = new VolumetricAtmosphere();
  readonly distantDepth=this.atmosphere.distantDepth;
  private softRT: THREE.WebGLRenderTarget[] = [];
  private inkRT: THREE.WebGLRenderTarget | null = null;
  private inkPreRT: THREE.WebGLRenderTarget | null = null;
  private downMat: THREE.ShaderMaterial | null = null;
  private blurMat: THREE.ShaderMaterial | null = null;
  private inkPreMat: THREE.ShaderMaterial | null = null;
  private inkMat: THREE.ShaderMaterial | null = null;
  private compMat: THREE.ShaderMaterial | null = null;
  private grainTex: THREE.DataTexture | null = null;
  private quad: THREE.Mesh | null = null;
  private quadScene = new THREE.Scene();
  private quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private width = 1;
  private height = 1;
  /** Set false to fall straight through to `renderer.render` and allocate nothing. */
  enabled = true;

  setSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    if (this.sceneRT) this.resizeTargets();
  }

  /** True once the chain has been built. */
  get allocated(): boolean { return this.sceneRT !== null; }

  private bufferSize(renderer: THREE.WebGLRenderer): { w: number; h: number } {
    const r = renderer.getPixelRatio();
    return {
      w: Math.max(1, Math.floor(this.width * r)),
      h: Math.max(1, Math.floor(this.height * r)),
    };
  }

  private pixelRatio = 1;
  private logDepth = false;
  private srgbScene = true;
  /** Scratch for the ink anchor math — a per-frame render allocates nothing. */
  private _m4 = new THREE.Matrix4();
  private _rotM = new THREE.Matrix3();
  private _camM = new THREE.Matrix3();
  private _base = new THREE.Vector3();

  private resizeTargets(): void {
    const w = Math.max(1, Math.floor(this.width * this.pixelRatio));
    const h = Math.max(1, Math.floor(this.height * this.pixelRatio));
    this.sceneRT?.setSize(w, h);
    const sw = Math.max(2, w >> SOFT_SHIFT);
    const sh = Math.max(2, h >> SOFT_SHIFT);
    for (const rt of this.softRT) rt.setSize(sw, sh);
    const iw = Math.max(2, w >> 1);
    const ih = Math.max(2, h >> 1);
    this.inkRT?.setSize(iw, ih);
    this.inkPreRT?.setSize(iw, ih);
    if (this.inkMat) this.inkMat.uniforms.uMaskRes.value.set(iw, ih);
    if (this.inkPreMat) {
      this.inkPreMat.uniforms.uMaskRes.value.set(iw, ih);
      this.inkPreMat.uniforms.uRes.value.set(w, h);
    }
    if (this.compMat) this.compMat.uniforms.uRes.value.set(w, h);
  }

  private build(renderer: THREE.WebGLRenderer): void {
    this.pixelRatio = renderer.getPixelRatio();
    this.logDepth = renderer.capabilities.logarithmicDepthBuffer === true;
    const { w, h } = this.bufferSize(renderer);

    // The depth attachment is a real texture so the composite can ask how far
    // away each pixel is. HalfFloat colour keeps the scene linear and unclipped
    // until the composite tonemaps it.
    const depth = new THREE.DepthTexture(w, h, THREE.UnsignedIntType);
    depth.format = THREE.DepthFormat;
    depth.minFilter = THREE.NearestFilter;
    depth.magFilter = THREE.NearestFilter;
    const srgb = PAINTERLY.blendSpace === "srgb";
    this.srgbScene = srgb;
    this.sceneRT = new THREE.WebGLRenderTarget(w, h, {
      // In "srgb" the buffer mirrors the canvas exactly: eight bits, encoded
      // values, blending where the game's effects expect it. In "linear" it is
      // half-float so the tonemap has real headroom to roll off.
      type: srgb ? THREE.UnsignedByteType : THREE.HalfFloatType,
      samples: SAMPLES,
      depthBuffer: true,
      depthTexture: depth,
    });
    if (srgb) {
      // The three halves of the trick, and they only work together — see
      // BLEND_SPACE_NOTE. None is meaningful on its own.
      this.sceneRT.texture.colorSpace = THREE.SRGBColorSpace;
      (this.sceneRT as unknown as { isXRRenderTarget: boolean }).isXRRenderTarget = true;
      // Pin the storage. Only the MULTISAMPLE renderbuffer honours
      // forceLinearTransfer; the resolve texture would still be picked as
      // SRGB8_ALPHA8 from its colour space, and glBlitFramebuffer refuses to
      // resolve between mismatched formats — a screenful of GL_INVALID_OPERATION
      // per frame. Naming the format explicitly short-circuits both paths to the
      // same RGBA8, which is also what keeps the sampler from decoding values
      // the composite is about to decode itself.
      this.sceneRT.texture.internalFormat = "RGBA8";
    }

    const sw = Math.max(2, w >> SOFT_SHIFT);
    const sh = Math.max(2, h >> SOFT_SHIFT);
    for (let i = 0; i < 2; i++) {
      const rt = new THREE.WebGLRenderTarget(sw, sh, { type: THREE.HalfFloatType, depthBuffer: false });
      rt.texture.minFilter = rt.texture.magFilter = THREE.LinearFilter;
      this.softRT.push(rt);
    }

    this.grainTex = makeGrainTexture();

    this.downMat = new THREE.ShaderMaterial({
      vertexShader: QUAD_VS,
      fragmentShader: DOWN_FS,
      uniforms: { uSrc: { value: null }, uTexel: { value: new THREE.Vector2() } },
      depthTest: false,
      depthWrite: false,
    });
    this.blurMat = new THREE.ShaderMaterial({
      vertexShader: QUAD_VS,
      fragmentShader: BLUR_FS,
      uniforms: { uSrc: { value: null }, uDir: { value: new THREE.Vector2() } },
      depthTest: false,
      depthWrite: false,
    });

    // The half-res ink chain: pre-pass field (NEAREST — the mask must read its
    // texels exactly), then the mask (LINEAR — the composite's bilinear read
    // is what turns mask texels into soft, stable, thick lines).
    const iw = Math.max(2, w >> 1);
    const ih = Math.max(2, h >> 1);
    this.inkPreRT = new THREE.WebGLRenderTarget(iw, ih, {
      type: THREE.HalfFloatType,
      depthBuffer: false,
    });
    this.inkPreRT.texture.minFilter = this.inkPreRT.texture.magFilter = THREE.NearestFilter;
    this.inkPreMat = new THREE.ShaderMaterial({
      vertexShader: QUAD_VS,
      fragmentShader: INK_PRE_FS,
      uniforms: {
        uDepth: { value: depth },
        uScene: { value: this.sceneRT.texture },
        uRes: { value: new THREE.Vector2(w, h) },
        uMaskRes: { value: new THREE.Vector2(iw, ih) },
        uDepthK: { value: new THREE.Vector3(1, 1, 1) },
      },
      defines: { ...(renderer.capabilities.logarithmicDepthBuffer ? { LOG_DEPTH: "" } : {}) },
      depthTest: false,
      depthWrite: false,
    });
    this.inkRT = new THREE.WebGLRenderTarget(iw, ih, { depthBuffer: false });
    this.inkRT.texture.minFilter = this.inkRT.texture.magFilter = THREE.LinearFilter;
    this.inkMat = new THREE.ShaderMaterial({
      vertexShader: QUAD_VS,
      fragmentShader: INK_FS,
      uniforms: {
        uPre: { value: this.inkPreRT.texture },
        uMaskRes: { value: new THREE.Vector2(iw, ih) },
        uInkWidth: { value: 1 },
      },
      depthTest: false,
      depthWrite: false,
    });
    this.compMat = new THREE.ShaderMaterial({
      vertexShader: QUAD_VS,
      fragmentShader: COMPOSITE_FS,
      uniforms: {
        uScene: { value: this.sceneRT.texture },
        uSoft: { value: this.softRT[0].texture },
        uVolume: { value: null }, uVolumeDepth: { value: null },
        uVolumeSize: { value: new THREE.Vector2(1, 1) }, uVolumeOn: { value: 0 },...this.distantDepth.uniforms,
        uDepth: { value: depth },
        uGrainTex: { value: this.grainTex },
        uRes: { value: new THREE.Vector2(w, h) },
        uAspect: { value: w / h },
        uDepthK: { value: new THREE.Vector3(1, 1, 1) },
        uFogDensity: { value: 0 },
        uPaint: { value: 1 },
        uExposure: { value: 1 },
        uGrain: { value: 1 },
        uVignette: { value: 0.85 },
        uCA: { value: 1 },
        uSoften: { value: PAINTERLY.soften },
        uSpeed: { value: 0 },
        uSat: { value: PAINTERLY.sat },
        uHeritage: { value: PAINTERLY.heritage },
        uBloom: { value: PAINTERLY.bloom },
        uInk: { value: PAINTERLY.ink },
        uInkBreak: { value: 1 },
        uInkDay: { value: 1 },
        uInkTex: { value: this.inkRT.texture },
        uCamTan: { value: new THREE.Vector2(1, 1) },
        uCamBasis: { value: new THREE.Matrix3() },
        uInkSnap: { value: new THREE.Vector3() },
      },
      defines: {
        ...(renderer.capabilities.logarithmicDepthBuffer ? { LOG_DEPTH: "" } : {}),
        ...(srgb ? { SRGB_SCENE: "" } : {}),
      },
      depthTest: false,
      depthWrite: false,
    });

    // One oversized triangle beats two triangles: no seam down the diagonal, and
    // the quad is never re-projected because the vertex stage ignores the camera.
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    geom.setAttribute("uv", new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    this.quad = new THREE.Mesh(geom, this.compMat);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
  }

  private blit(renderer: THREE.WebGLRenderer, mat: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget | null): void {
    if (!this.quad) return;
    this.quad.material = mat;
    renderer.setRenderTarget(target);
    renderer.render(this.quadScene, this.quadCam);
  }

  /**
   * Draw the frame. `speed` is the 0..1 vehicle speed cue; `fogDensity` is the
   * scene's FogExp2 density (0 in space, which switches the distance softening
   * off by itself). At `enabled = false` this is exactly `renderer.render`.
   *
   * `opts.drawScene` replaces the single `renderer.render` with whatever the
   * caller uses to put the scene on screen — in the game that is the depth
   * partition's multi-pass loop (world/depthPartition.ts). When it is supplied,
   * `opts.depth` MUST describe the slice the surviving depth buffer belongs to:
   * partitioned rendering clears depth between slices, so the buffer this pass
   * reads holds only the nearest one, and linearising it against the camera's
   * full range would put every pixel at the wrong distance.
   */
  render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    speed: number,
    fogDensity: number,
    opts?: {
      drawScene?: (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera) => void;
      depth?: { near: number; far: number };
      /** Frame the ink breakup noise anchors to — the foot planet's pose plus
       *  the floating origin. Omitted (space, flight), it anchors to render
       *  space instead. See the anchor note in the ink block. */
      inkFrame?: { center: THREE.Vector3; invQuat: THREE.Quaternion; origin: THREE.Vector3 };
      /** 0 night → 1 day. By day the ink's shadow gate is active (no lines in
       *  cast shadow); by night it blends out so borders stay drawn, full
       *  black. Defaults to 1 (day behaviour) when omitted. */
      day?: number;
      atmosphere?: AtmosphereFrame;
    },
  ): void {
    const draw = opts?.drawScene ?? ((r, s, c) => r.render(s, c));
    if (!this.enabled) {
      this.atmosphere.reset();
      renderer.setRenderTarget(null);
      draw(renderer, scene, camera);
      return;
    }
    // A live blendSpace flip changes the target's format, so the chain has to be
    // rebuilt rather than re-poked. Console-only, and cheap enough at that.
    if (this.sceneRT && this.srgbScene !== (PAINTERLY.blendSpace === "srgb")) this.dispose();
    if (!this.sceneRT) this.build(renderer);
    if (this.pixelRatio !== renderer.getPixelRatio()) {
      this.pixelRatio = renderer.getPixelRatio();
      this.resizeTargets();
    }
    const sceneRT = this.sceneRT!;
    const comp = this.compMat!;

    renderer.setRenderTarget(sceneRT);
    draw(renderer, scene, camera);

    // GPU attribution: the volume march, then everything after it is "post".
    perf.gpuBegin("volume");
    const volume = opts?.atmosphere ? this.atmosphere.render(renderer, camera, sceneRT.depthTexture!,
      sceneRT.width, sceneRT.height, opts.depth?.near ?? camera.near, opts.depth?.far ?? camera.far, opts.atmosphere) : null;
    if (!opts?.atmosphere) this.atmosphere.reset();
    perf.gpuBegin("post");
    comp.uniforms.uVolumeOn.value = volume ? 1 : 0;
    if (volume) {
      comp.uniforms.uVolume.value = volume.texture;
      comp.uniforms.uVolumeDepth.value = volume.depthTexture;
      comp.uniforms.uVolumeSize.value.set(volume.width, volume.height);
    }

    // ── the soft ("wet-in-wet") chain, at 1/64 the pixels ──────────────────
    const down = this.downMat!;
    down.uniforms.uSrc.value = sceneRT.texture;
    down.uniforms.uTexel.value.set(1 / sceneRT.width, 1 / sceneRT.height);
    this.blit(renderer, down, this.softRT[0]);

    const blur = this.blurMat!;
    const sw = this.softRT[0].width, sh = this.softRT[0].height;
    blur.uniforms.uSrc.value = this.softRT[0].texture;
    blur.uniforms.uDir.value.set(1 / sw, 0);
    this.blit(renderer, blur, this.softRT[1]);
    blur.uniforms.uSrc.value = this.softRT[1].texture;
    blur.uniforms.uDir.value.set(0, 1 / sh);
    this.blit(renderer, blur, this.softRT[0]);

    // ── the print ──────────────────────────────────────────────────────────
    const u = comp.uniforms;
    u.uScene.value = sceneRT.texture;
    u.uSoft.value = this.softRT[0].texture;
    u.uAspect.value = sceneRT.width / Math.max(1, sceneRT.height);
    // Which range the depth buffer actually spans — the partition's nearest
    // slice when partitioning, the camera otherwise.
    const dNear = opts?.depth?.near ?? camera.near;
    const dFar = opts?.depth?.far ?? camera.far;
    if (this.logDepth) u.uDepthK.value.set(Math.log2(dFar + 1.0), 0, 0);
    else u.uDepthK.value.set(dNear, dFar, dFar - dNear);
    u.uFogDensity.value = fogDensity;
    u.uPaint.value = PAINTERLY.paint;
    u.uExposure.value = PAINTERLY.exposure;
    u.uGrain.value = PAINTERLY.grain;
    u.uVignette.value = PAINTERLY.vignette;
    u.uCA.value = PAINTERLY.ca;
    u.uSoften.value = PAINTERLY.soften;
    u.uSpeed.value = speed;
    u.uSat.value = PAINTERLY.sat;
    u.uHeritage.value = PAINTERLY.heritage;
    u.uBloom.value = PAINTERLY.bloom;
    u.uInk.value = PAINTERLY.ink;
    u.uInkBreak.value = PAINTERLY.inkBreak;
    u.uInkDay.value = Math.max(0, Math.min(1, opts?.day ?? 1));
    // The ink block reconstructs positions from depth: camera basis, the
    // frustum half-tangents, and a base position modulo the 64 m noise tile
    // (kept small so the grain lookup never runs out of float precision — the
    // same trick, and the same tile discipline, as the paint noise's snap).
    // With an inkFrame, basis and base are both rotated into the planet's
    // local frame FIRST — the rotation does not commute with the modulo, and
    // it is the local frame the gaps have to hold still in.
    {
      const tanH = Math.tan((camera.fov * Math.PI) / 360);
      u.uCamTan.value.set(tanH * (sceneRT.width / Math.max(1, sceneRT.height)), tanH);
      this._camM.setFromMatrix4(camera.matrixWorld);
      const e = camera.matrixWorld.elements;
      const fr = opts?.inkFrame;
      if (fr) {
        this._rotM.setFromMatrix4(this._m4.makeRotationFromQuaternion(fr.invQuat));
        (u.uCamBasis.value as THREE.Matrix3).multiplyMatrices(this._rotM, this._camM);
        this._base
          .set(e[12] + fr.origin.x - fr.center.x, e[13] + fr.origin.y - fr.center.y, e[14] + fr.origin.z - fr.center.z)
          .applyMatrix3(this._rotM);
      } else {
        (u.uCamBasis.value as THREE.Matrix3).copy(this._camM);
        this._base.set(e[12], e[13], e[14]);
      }
      const SNAP = 64;
      u.uInkSnap.value.set(
        this._base.x - Math.floor(this._base.x / SNAP) * SNAP,
        this._base.y - Math.floor(this._base.y / SNAP) * SNAP,
        this._base.z - Math.floor(this._base.z / SNAP) * SNAP,
      );
    }

    // ── the ink chain, half res (see INK_PRE_FS / INK_FS) ──────────────────
    // Skipped entirely at ink = 0 or paint = 0; the composite's own inkK guard
    // means the stale mask is never read, so the passthrough stays honest.
    if (PAINTERLY.ink * PAINTERLY.paint > 0.001) {
      const pre = this.inkPreMat!;
      pre.uniforms.uScene.value = sceneRT.texture;
      if (this.logDepth) pre.uniforms.uDepthK.value.set(Math.log2(dFar + 1.0), 0, 0);
      else pre.uniforms.uDepthK.value.set(dNear, dFar, dFar - dNear);
      this.blit(renderer, pre, this.inkPreRT!);
      const im = this.inkMat!;
      im.uniforms.uInkWidth.value = PAINTERLY.inkWidth;
      this.blit(renderer, im, this.inkRT!);
    }

    this.blit(renderer, comp, null);
  }

  dispose(): void {
    this.atmosphere.dispose();
    this.sceneRT?.dispose();
    this.sceneRT = null;
    for (const rt of this.softRT) rt.dispose();
    this.softRT = [];
    this.inkRT?.dispose();
    this.inkRT = null;
    this.inkPreRT?.dispose();
    this.inkPreRT = null;
    this.downMat?.dispose();
    this.blurMat?.dispose();
    this.inkPreMat?.dispose();
    this.inkMat?.dispose();
    this.compMat?.dispose();
    this.grainTex?.dispose();
    this.quad?.geometry.dispose();
    this.downMat = this.blurMat = this.inkPreMat = this.inkMat = this.compMat = null;
    this.grainTex = null;
    this.quad = null;
  }
}
